import { useTranslation } from 'react-i18next';
import {
  DndContext, closestCenter, useSensor, useSensors, MouseSensor, TouchSensor,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Route, Icon } from '../ui/Icon';
import Spinner from '../ui/Spinner';
import DayRouteSection from './DayRouteSection';
import type { TravelMode } from '../../api/directions.api';
import type { DayRouteGroup } from '../../utils/routeGroups';

interface Props {
  groups: DayRouteGroup[];
  destination: string;
  loading: boolean;
  error: string | null;
  collapsedDays: Set<number>;
  busyDays: Set<number>;
  optimizingDay: number | null;
  legUnavailable: Record<string, string[]>;
  selectedActivityId: string | null;
  /** Optional stops the filter is hiding. Empty when it is off. */
  hiddenStops: Set<string>;
  onToggleCollapsed: (dayIdx: number) => void;
  onSelectStop: (dayIdx: number, activityId: string) => void;
  /** Absent = read-only: somebody else's trip, seen from the community page. */
  edit?: RouteEditActions;
  hideHeader?: boolean;
}

export interface RouteEditActions {
  onSetLegMode: (dayIdx: number, legIdx: number, mode: TravelMode) => void;
  onReorder: (dayIdx: number, activeId: string, overId: string) => void;
  /** A stop dragged out of its day and dropped into another one. */
  onMoveToDay: (activityIds: string[], targetDayId: string, atIndex: number) => void;
  onOptimize: (dayIdx: number, hint?: string) => void;
}

/**
 * Every selected day's route in one scroll, grouped by day. Stop order and
 * numbering come from `buildDayRouteGroups`, the same helper the map pins read,
 * so the panel and the map can never disagree about which stop is which.
 */
export default function RoutesPanel({
  groups, destination, loading, error, collapsedDays, busyDays, optimizingDay,
  legUnavailable, selectedActivityId, hiddenStops,
  onToggleCollapsed, onSelectStop, edit, hideHeader,
}: Props) {
  const { t } = useTranslation();
  const totalStops = groups.reduce(
    (sum, g) => sum + g.stops.filter(s => !hiddenStops.has(s.activity.id)).length, 0,
  );

  // Touch needs press-and-hold to drag (see TimelineRail) — a distance-based
  // pointer drag on mobile gets swallowed by the scroll gesture.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const groupOf = (activityId: string) =>
    groups.find(g => g.stops.some(s => s.activity.id === activityId));

  /**
   * The context spans every day in the panel, so the same gesture means two
   * different things: dropped back into its own day it reorders, dropped into
   * another day's stops it moves. Only an expanded day renders a sortable list,
   * so a collapsed one is simply not a target.
   */
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!edit || !over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId   = String(over.id);
    const from = groupOf(activeId);
    const to   = groupOf(overId);
    if (!from || !to) return;

    if (from.dayIdx === to.dayIdx) { edit.onReorder(from.dayIdx, activeId, overId); return; }

    const stops = to.stops.filter(s => !s.activity.isOrigin);
    const atIndex = Math.max(0, stops.findIndex(s => s.activity.id === overId));
    edit.onMoveToDay([activeId], to.day.id, atIndex);
  }

  return (
    <>
      {!hideHeader && (
        <div className="shrink-0 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <p className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest">
            {t('map.daysCount', { count: groups.length })} · {t('map.stopsCount', { count: totalStops })}
          </p>
        </div>
      )}

      {loading && (
        <div className="shrink-0 flex items-center gap-2 px-4 py-2 bg-teal-50/60 dark:bg-teal-900/20 border-b border-teal-100 dark:border-teal-900/40">
          <Spinner size="sm" />
          <span className="text-xs text-teal-700 dark:text-teal-300 font-medium">{t('map.calculatingRoutes')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="shrink-0 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-900/40">
          <p className="text-[11px] text-amber-700 dark:text-amber-400">{error}</p>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        {groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-8 text-center px-4">
            <Icon as={Route} size={24} className="text-slate-200 dark:text-slate-700 mx-auto mb-2" />
            <p className="text-xs text-slate-400">{t('map.noDaysSelected')}</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          {groups.map(group => (
            <DayRouteSection
              key={group.day.id}
              group={group}
              destination={destination}
              collapsed={collapsedDays.has(group.dayIdx)}
              busy={busyDays.has(group.dayIdx)}
              optimizing={optimizingDay === group.dayIdx}
              legUnavailable={legUnavailable}
              selectedActivityId={selectedActivityId}
              hiddenStops={hiddenStops}
              onToggleCollapsed={() => onToggleCollapsed(group.dayIdx)}
              onSelectStop={id => onSelectStop(group.dayIdx, id)}
              edit={edit && {
                onSetLegMode: (legIdx, mode) => edit.onSetLegMode(group.dayIdx, legIdx, mode),
                onOptimize: hint => edit.onOptimize(group.dayIdx, hint),
              }}
            />
          ))}
          </DndContext>
        )}
      </div>
    </>
  );
}
